import { useEffect, useState } from 'react';
import { Form, Modal, Tabs, message } from 'antd';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCreatePatientMutation } from '@/state/slices/patients';
import { useAssignStudyPatientMutation } from '@/state/slices/uploads';
import type {
  CreatePatientPayload,
  Patient,
  PatientListItem,
} from '@/models/patient';
import type { UploadRow } from '@/models/upload';
import LinkExistingTab from './LinkExistingTab';
import CreateNewTab, { type CreatePatientFormValues } from './CreateNewTab';
import styles from './AssignPatientModal.module.css';

interface AssignPatientModalProps {
  open: boolean;
  upload: UploadRow | null;
  onClose: () => void;
}

const trimOptional = (v: string | undefined): string | undefined => {
  const t = v?.trim();
  return t || undefined;
};

type TabKey = 'link' | 'create';
type BackendErrorPayload = Partial<
  Record<keyof CreatePatientPayload, string[]>
>;

export default function AssignPatientModal({
  open,
  upload,
  onClose,
}: AssignPatientModalProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('link');
  const [selectedPatient, setSelectedPatient] =
    useState<PatientListItem | null>(null);
  const [createForm] = Form.useForm<CreatePatientFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [assignPatient, { isLoading: isAssigning }] =
    useAssignStudyPatientMutation();
  const [createPatient, { isLoading: isCreating }] = useCreatePatientMutation();

  // Reset state whenever the modal opens or the target study changes — we
  // never want to carry a search query / selection between studies.
  useEffect(() => {
    if (open) {
      setActiveTab('link');
      setSelectedPatient(null);
      createForm.resetFields();
    }
  }, [open, upload?.uuid, createForm]);

  // Only rows with a study can open this modal (R9); the PATCH addresses
  // the study, not the upload (R10).
  const studyUuid = upload?.studyUuid;
  if (!upload || !studyUuid) return null;

  const isBusy = isAssigning || isCreating;
  const currentPatientName = [upload.patientFirstName, upload.patientLastName]
    .filter(Boolean)
    .join(' ');
  const currentMrn = upload.patientMedicalRecordNumber;
  const showCurrentBanner =
    !upload.needsPatientAssignment && !!currentPatientName;

  const handleLinkConfirm = async () => {
    if (!selectedPatient) return;
    try {
      await assignPatient({
        studyUuid,
        patientUuid: selectedPatient.uuid,
      }).unwrap();
      messageApi.success(
        `Linked study to ${selectedPatient.fullName || selectedPatient.medicalRecordNumber}`
      );
      onClose();
    } catch {
      messageApi.error('Failed to link patient');
    }
  };

  const handleCreateAndAssign = async (values: CreatePatientFormValues) => {
    // BE expects a single `referringPhysician` string (it splits on whitespace
    // after stripping a leading honorific). The form has split first/last
    // inputs for UX, so combine here. Mirrors NewPatientModal.
    const referringFirst = values.referringPhysicianFirstName?.trim() ?? '';
    const referringLast = values.referringPhysicianLastName?.trim() ?? '';
    const referringPhysician =
      `${referringFirst} ${referringLast}`.trim() || undefined;

    const payload: CreatePatientPayload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      medicalRecordNumber: values.medicalRecordNumber.trim(),
      dateOfBirth: values.dateOfBirth.format('YYYY-MM-DD'),
      sex: values.sex,
      referringPhysician,
      phoneNumber: trimOptional(values.phoneNumber),
      address: trimOptional(values.address),
      ethnicity: trimOptional(values.ethnicity),
      notes: trimOptional(values.notes),
    };

    let created: Patient;
    try {
      created = await createPatient(payload).unwrap();
    } catch (err) {
      const data = (err as FetchBaseQueryError | undefined)?.data as
        | BackendErrorPayload
        | undefined;
      if (data && typeof data === 'object') {
        const fieldErrors = (
          Object.keys(data) as (keyof CreatePatientPayload)[]
        )
          .filter((k) => Array.isArray(data[k]))
          .map((rawKey) => {
            // BE returns errors keyed by `referringPhysician`; route them to
            // the first-name input so they render under the visible field.
            const formKey: keyof CreatePatientFormValues =
              rawKey === 'referringPhysician'
                ? 'referringPhysicianFirstName'
                : (rawKey as keyof CreatePatientFormValues);
            return { name: formKey, errors: data[rawKey] as string[] };
          });
        if (fieldErrors.length) {
          createForm.setFields(fieldErrors);
          return;
        }
      }
      messageApi.error('Failed to create patient');
      return;
    }

    try {
      await assignPatient({
        studyUuid,
        patientUuid: created.uuid,
      }).unwrap();
      messageApi.success(
        `Created ${created.firstName ?? ''} ${created.lastName ?? ''} and linked the study`.trim()
      );
      onClose();
    } catch {
      // Patient was created but linking failed — the user can retry from
      // the Link tab; the new patient is now searchable.
      messageApi.warning(
        'Patient created, but linking to the study failed. Try again from the Link existing tab.'
      );
    }
  };

  const handleOk = async () => {
    if (activeTab === 'link') {
      await handleLinkConfirm();
    } else {
      // Triggers form's onFinish, which calls handleCreateAndAssign.
      createForm.submit();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Assign patient"
        open={open}
        onCancel={onClose}
        onOk={handleOk}
        okText={activeTab === 'link' ? 'Link patient' : 'Create & link'}
        okButtonProps={{
          loading: isBusy,
          disabled: (activeTab === 'link' && !selectedPatient) || isBusy,
        }}
        cancelButtonProps={{ disabled: isBusy }}
        width={680}
        destroyOnHidden
        // Anchor below the 56px app header (Header.module.css:6) + 16px margin.
        // `centered` would override this and tuck the top edge behind the
        // header when the Create-new form makes the modal tall.
        style={{ top: 72 }}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            paddingRight: 4,
          },
        }}
      >
        {showCurrentBanner && (
          <div className={styles.currentBanner}>
            Currently linked to <strong>{currentPatientName}</strong>
            {currentMrn ? ` (${currentMrn})` : ''}. Pick a different patient or
            create a new one to re-point this study.
          </div>
        )}
        <Tabs
          className={styles.tabs}
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as TabKey)}
          items={[
            {
              key: 'link',
              label: 'Link existing',
              children: (
                <LinkExistingTab
                  selectedUuid={selectedPatient?.uuid ?? null}
                  onSelect={setSelectedPatient}
                />
              ),
            },
            {
              key: 'create',
              label: 'Create new',
              children: (
                <CreateNewTab
                  form={createForm}
                  onSubmit={handleCreateAndAssign}
                />
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
